"use client";

import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { isSafeFormFunnelContentHref } from "@portal/app/_common/lib/formFunnelContent";
import type { SerializedLinkNode, SerializedAutoLinkNode } from "@payloadcms/richtext-lexical";
import type { JSXConverter, JSXConvertersFunction } from "@payloadcms/richtext-lexical/react";
import { RichText } from "@payloadcms/richtext-lexical/react";
import type { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import { useFormFunnelHeadingLevel } from "./FormFunnelHeadingContext";

interface FormFunnelRichTextProps {
  content?: SerializedEditorState | Record<string, unknown>;
  promoteFirstHeading?: boolean;
  tone?: "body" | "finePrint";
  testId?: string;
  appearance?: "article" | "footer" | "interstitial";
}

const safeLink: JSXConverter<SerializedLinkNode | SerializedAutoLinkNode> = ({
  node,
  nodesToJSX,
}) => {
  const children = nodesToJSX({ nodes: node.children });
  const href = node.fields.url;
  if (node.fields.linkType === "internal" || !isSafeFormFunnelContentHref(href)) {
    return <>{children}</>;
  }
  return (
    <a
      href={href?.trim()}
      target={node.fields.newTab ? "_blank" : undefined}
      rel={node.fields.newTab ? "noopener noreferrer" : undefined}
    >
      {children}
    </a>
  );
};

const converters: JSXConvertersFunction = ({ defaultConverters }) => ({
  ...defaultConverters,
  link: safeLink,
  autolink: safeLink,
});

// The active step owns the page h1. Preserve authored visual sizing while
// rendering static article/disclosure headings at a subordinate semantic level.
const contentConverters: JSXConvertersFunction = ({ defaultConverters }) => ({
  ...defaultConverters,
  link: safeLink,
  autolink: safeLink,
  heading: ({ node, nodesToJSX }) => {
    const Tag = node.tag === "h1" ? "h2" : node.tag;
    return (
      <Tag data-original-heading={node.tag === "h1" ? "h1" : undefined}>
        {nodesToJSX({ nodes: node.children })}
      </Tag>
    );
  },
});

/** Promote the first configured heading and prevent duplicate h1 elements. */
export function normalizeFormFunnelHeadings(
  value: unknown,
  hasPrimaryHeading = { value: false }
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeFormFunnelHeadings(item, hasPrimaryHeading));
  }
  if (!value || typeof value !== "object") return value;

  const node = value as Record<string, unknown>;
  const normalized = Object.fromEntries(
    Object.entries(node).map(([key, child]) => [
      key,
      normalizeFormFunnelHeadings(child, hasPrimaryHeading),
    ])
  );

  if (node.type === "heading") {
    if (!hasPrimaryHeading.value) {
      hasPrimaryHeading.value = true;
      normalized.tag = "h1";
    } else if (node.tag === "h1") {
      normalized.tag = "h2";
    }
  }

  return normalized;
}

export function FormFunnelRichText({
  content,
  promoteFirstHeading = false,
  tone = "body",
  testId,
  appearance = "article",
}: FormFunnelRichTextProps) {
  const headingLevel = useFormFunnelHeadingLevel();
  if (!content || !("root" in content)) return null;

  const normalizedContent = promoteFirstHeading
    ? (normalizeFormFunnelHeadings(content) as SerializedEditorState)
    : (content as SerializedEditorState);

  const rendered = (
    <RichText
      data={normalizedContent}
      converters={promoteFirstHeading && headingLevel === "h1" ? converters : contentConverters}
      disableContainer={appearance === "footer"}
    />
  );
  if (appearance === "footer")
    return <FooterRichTextRoot data-testid={testId}>{rendered}</FooterRichTextRoot>;
  if (appearance === "interstitial")
    return <InterstitialRichTextRoot data-testid={testId}>{rendered}</InterstitialRichTextRoot>;

  return (
    <RichTextRoot $finePrint={tone === "finePrint"} data-testid={testId}>
      {rendered}
    </RichTextRoot>
  );
}

const RichTextRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$finePrint",
})<{ $finePrint: boolean }>`
  width: 100%;
  color: ${({ theme }) => theme.palette.dream.grayDark};
  text-align: left;

  h1,
  h2,
  h3,
  h4 {
    margin: 28px 0 12px;
    color: ${({ theme }) => theme.palette.primary.main};
    font-weight: 700;
    line-height: 1.2;
  }

  h1:first-child,
  h2:first-child,
  h3:first-child,
  h4:first-child {
    margin-top: 0;
  }

  h2 {
    font-size: ${({ $finePrint }) => ($finePrint ? "0.95rem" : "1.5rem")};
  }

  h1,
  h2[data-original-heading="h1"] {
    font-size: ${({ $finePrint }) => ($finePrint ? "1rem" : "2rem")};
  }

  h3 {
    font-size: ${({ $finePrint }) => ($finePrint ? "0.9rem" : "1.2rem")};
  }

  h4 {
    font-size: ${({ $finePrint }) => ($finePrint ? "0.85rem" : "1rem")};
  }

  p,
  li {
    font-size: ${({ $finePrint }) => ($finePrint ? "0.75rem" : "1rem")};
    line-height: ${({ $finePrint }) => ($finePrint ? 1.55 : 1.65)};
  }

  p {
    margin: 0 0 ${({ $finePrint }) => ($finePrint ? "8px" : "16px")};
  }

  ul,
  ol {
    margin: 0 0 16px;
    padding-left: 26px;
  }

  li + li {
    margin-top: 6px;
  }

  a {
    color: ${({ theme }) => theme.palette.primary.main};
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }

  > :last-child,
  > :last-child > :last-child {
    margin-bottom: 0;
  }
`;

const FooterRichTextRoot = styled(Box)`
  width: 100%;
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  line-height: ${({ theme }) => theme.typography.body2.lineHeight};
  color: ${({ theme }) => theme.palette.text.secondary};
  text-align: center;

  p {
    margin: 0 0 8px 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 8px 0;
    color: ${({ theme }) => theme.palette.dream.grayDark};
    line-height: 1.3;
  }

  h2 {
    font-size: 1.15rem;
  }

  h1,
  h2[data-original-heading="h1"] {
    font-size: 1.25rem;
  }

  h3,
  h4,
  h5,
  h6 {
    font-size: 1.05rem;
  }

  strong,
  b {
    color: ${({ theme }) => theme.palette.dream.grayDark};
  }

  a {
    color: ${({ theme }) => theme.palette.primary.main};
    text-decoration: underline;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  ul,
  ol {
    display: inline-block;
    padding-left: 20px;
    margin: 0 0 8px 0;
    text-align: left;
  }

  > :last-child {
    margin-bottom: 0;
  }
`;

const InterstitialRichTextRoot = styled(Box)`
  width: 100%;

  h1,
  h2,
  h3,
  h4 {
    color: #3f3770;
    margin-top: 0;
  }

  h2[data-original-heading="h1"] {
    font-size: 2em;
  }

  p {
    color: #4a4a4a;
    line-height: 1.6;
    font-size: 16px;
  }

  a {
    color: #3f3770;
    text-decoration: underline;
  }

  ul,
  ol {
    padding-left: 24px;
    color: #4a4a4a;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }
`;
